// Parse UI payload from Claude's response

import type { CustomDomain } from "@/lib/api/custom-domains";
import type { AskQuestionItem } from "@optidev/shared-types";

interface ActivityEvent {
  type: "thought" | "read" | "write" | "edit";
  duration_seconds?: number;
  path?: string;
}

interface FileChange {
  file: string;
  diff: string;
}

interface NewFile {
  file: string;
  contents: string;
}

interface Action {
  // Legacy action type (backward compatibility)
  kind?: "preview_latest" | "update_preview_image" | "restore";
  // New action properties for HITL buttons
  id?: string;
  label?: string;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  icon?: string;
  // Optional description shown below the button
  description?: string;
}

interface ThemeContext {
  themeId: string; // Unique identifier (e.g., "custom-abc123")
  name: string; // Display name (e.g., "Ocean Blue Theme")
  colors: string[]; // 4 preview hex colors
  content: string; // Full theme JSON as stringified JSON
  // When true, the frontend auto-saves + applies the theme on first
  // mount per (theme content × project), gated by a localStorage set
  // so reopening an old session does NOT re-apply. Set by
  // `generate_theme_hitl` in the sidecar.
  autoApply?: boolean;
}

interface PlanContext {
  plans: Array<{
    ref: string; // e.g. "#1"
    filePath: string; // e.g. ".local/plans/ai-slide-deck.md"
    title: string;
    artifactKinds: string[]; // e.g. ["slides"]
  }>;
}

interface CacheTtlContext {
  endpointPath: string; // gateway path, e.g. "/functions/v1/get-announcements"
  requestedTtl?: number; // value the user asked for (seconds); prefills the input
}

interface MediaResult {
  id: string;
  title: string;
  provider: "unsplash" | "pexels" | "pixabay" | "freesound";
  previewUrl: string;
  downloadUrl?: string;
  author: string;
  authorUrl?: string;
  width?: number;
  height?: number;
  duration?: number; // For videos/audio in seconds
  license?: string;
}

// Reference-only OptiSigns asset descriptor (optisigns_asset_results HITL).
// Never contains base64 — only ids, names, and signed/external URLs.
interface OptisignsAssetItem {
  assetId: string;
  name: string;
  type: string;
  fileType?: string;
  webType?: string;
  appType?: string;
  thumbnailUrl?: string | null;
  thumbnailIsAppIcon?: boolean;
  sourceUrl?: string | null;
  sourceKind?: "cdn" | "web_link" | "embed" | null;
  numPages?: number;
  duration?: number;
  path?: string | null;
}

interface OptisignsFolderItem {
  folderId: string;
  name: string;
  path?: string | null;
  thumbnailUrl?: string | null;
}

// Pending OptiSigns write awaiting user confirmation (optisigns_write_confirm HITL).
// `op` + `args` are echoed back on Confirm so the agent re-invokes the exact same
// write tool with `confirmed: true`. Never contains credentials.
interface OptisignsWriteConfirm {
  assetName: string;
  assetId?: string;
  screenName: string;
  deviceIds?: string[];
  op: string;
  args: Record<string, unknown>;
  // Generic destructive-action confirm (delete / remove / unpair / cancel):
  // `confirmKind: 'generic'` renders the summary/title/detail instead of the
  // assign-specific "asset → screen" line.
  confirmKind?: "assign" | "generic";
  title?: string;
  detail?: string;
}

interface UIPayload {
  // HITL type for specialized rendering (approve_plan, activate_optidev_cloud, setup_connection, theme_approval, media_library_results, optisigns_asset_results, start_building, etc.)
  hitl_type?: string;
  // Theme context for theme_approval HITL
  themeContext?: ThemeContext;
  // Plan context for start_building HITL (propose_project_plan_hitl tool)
  planContext?: PlanContext;
  // Connector type for setup_connection HITL (e.g., "stripe", "mysql")
  connectorType?: string;
  // Cache TTL context for cache_ttl HITL (per-endpoint Cache TTL override)
  cacheTtlContext?: CacheTtlContext;
  // Pending destructive OptiSigns write (optisigns_write_confirm HITL)
  optisignsWriteConfirm?: OptisignsWriteConfirm;

  // The custom domain the agent just added — for the custom_domain connect HITL.
  customDomain?: CustomDomain;

  // Media library results for media_library_results HITL
  mediaType?: "image" | "video" | "audio";
  query?: string;
  provider?: string;
  mediaResults?: MediaResult[];
  // When the agent searched multiple subjects in one call, results are
  // grouped by query. `mediaResults` (above) is still populated with the
  // flat union for back-compat with older renderers.
  groups?: Array<{ query: string; mediaResults: MediaResult[] }>;

  // OptiSigns asset/folder results for optisigns_asset_results HITL
  optisignsAssets?: OptisignsAssetItem[];
  optisignsFolders?: OptisignsFolderItem[];
  // Folder path currently being browsed (null/undefined = root).
  optisignsPath?: string | null;

  // Ask-user-question HITL (optidev_ask_user_question): structured clarifying
  // questions. `requestId` correlates the user's answers back to the blocked
  // tool; `questions` drives the interactive card.
  requestId?: string;
  questions?: AskQuestionItem[];

  headline: string;
  activity: ActivityEvent[];
  edits_count: number;
  changes: FileChange[];
  new_files: NewFile[];
  summary: string;
  actions: Action[];
  quick_checks: string[];
  notes: string[];
}

interface ParsedResponse {
  markdown: string;
  payload: UIPayload | null;
  rawContent: string;
}

/**
 * Extract UI payload from Claude's response
 * Looks for <ui_payload>...</ui_payload> tags
 */
function extractUIPayload(content: string): ParsedResponse {
  const payloadRegex = /<ui_payload>([\s\S]*?)<\/ui_payload>/;
  const match = content.match(payloadRegex);

  if (!match) {
    return {
      markdown: content,
      payload: null,
      rawContent: content,
    };
  }

  const payloadJson = match[1].trim();
  const markdown = content.replace(payloadRegex, "").trim();

  try {
    const payload = JSON.parse(payloadJson) as UIPayload;
    return {
      markdown,
      payload,
      rawContent: content,
    };
  } catch (error) {
    console.error("[PayloadParser] Failed to parse UI payload:", error);
    return {
      markdown: content,
      payload: null,
      rawContent: content,
    };
  }
}

/**
 * Extract partial UI payload from incomplete streaming response
 * Tries to parse whatever is available even if JSON is incomplete
 */
function extractPartialUIPayload(content: string): Partial<UIPayload> | null {
  const startTag = content.indexOf("<ui_payload>");
  if (startTag === -1) return null;

  const jsonStart = startTag + "<ui_payload>".length;
  const jsonContent = content.substring(jsonStart).trim();

  // Try to parse incomplete JSON by fixing common issues
  try {
    // Add closing braces/brackets if missing
    let fixedJson = jsonContent;

    // Count open/close braces and brackets
    const openBraces = (fixedJson.match(/\{/g) || []).length;
    const closeBraces = (fixedJson.match(/\}/g) || []).length;
    const openBrackets = (fixedJson.match(/\[/g) || []).length;
    const closeBrackets = (fixedJson.match(/\]/g) || []).length;

    // Add missing closing brackets
    fixedJson += "]".repeat(openBrackets - closeBrackets);
    // Add missing closing braces
    fixedJson += "}".repeat(openBraces - closeBraces);

    const partial = JSON.parse(fixedJson);
    return partial;
  } catch (error) {
    // If parsing fails, try to extract individual fields manually
    try {
      const partial: Partial<UIPayload> = {};

      // Extract activity array if present
      const activityMatch = jsonContent.match(/"activity"\s*:\s*(\[[^\]]*\])/);
      if (activityMatch) {
        try {
          partial.activity = JSON.parse(
            activityMatch[1] +
              "]".repeat(
                (activityMatch[1].match(/\[/g) || []).length -
                  (activityMatch[1].match(/\]/g) || []).length,
              ),
          );
        } catch {}
      }

      // Extract edits_count if present
      const editsMatch = jsonContent.match(/"edits_count"\s*:\s*(\d+)/);
      if (editsMatch) {
        partial.edits_count = parseInt(editsMatch[1], 10);
      }

      return Object.keys(partial).length > 0 ? partial : null;
    } catch {
      return null;
    }
  }
}

/**
 * Filter out tool usage messages (🔧 Using tool:...)
 */
function filterToolNoise(content: string): string {
  // Remove lines starting with 🔧 or "Using tool:"
  return content
    .split("\n")
    .filter(
      (line) => !line.trim().startsWith("🔧") && !line.includes("Using tool:"),
    )
    .join("\n")
    .trim();
}

/**
 * Calculate total thought time from activity events
 */
function getTotalThoughtTime(activity: ActivityEvent[]): number {
  return activity
    .filter((a) => a.type === "thought")
    .reduce((sum, a) => sum + (a.duration_seconds || 0), 0);
}

/**
 * Get file paths that were read
 */
function getReadFiles(activity: ActivityEvent[]): string[] {
  return activity
    .filter((a) => a.type === "read" && a.path)
    .map((a) => a.path!);
}

/**
 * Get file paths that were written/edited
 */
function getModifiedFiles(activity: ActivityEvent[]): string[] {
  return activity
    .filter((a) => (a.type === "write" || a.type === "edit") && a.path)
    .map((a) => a.path!);
}

/**
 * Visual Editor Message Utilities
 */

interface VisualEditorData {
  command: string;
  elementContext?: Record<string, unknown>;
}

/**
 * Parse visual editor message to extract command and element context
 */
function parseVisualEditorMessage(content: string): VisualEditorData | null {
  // Check if message has element_context tag
  const elementContextMatch = content.match(
    /<element_context>([\s\S]*?)<\/element_context>/,
  );

  if (!elementContextMatch) {
    return null;
  }

  // Extract command (everything before element_context tag)
  const beforeContext = content.substring(
    0,
    content.indexOf("<element_context>"),
  );
  const command = beforeContext.trim();

  // Parse element context JSON
  let elementContext: Record<string, unknown> | undefined;
  try {
    elementContext = JSON.parse(elementContextMatch[1]);
  } catch (e) {
    console.warn("[VisualEditor] Failed to parse element context:", e);
  }

  return {
    command,
    elementContext,
  };
}

/**
 * Get element info summary for display (e.g., "<button> at line 42 in Hero.tsx")
 * Note: Bind information is sent to Claude but not displayed in the UI badge
 */
function getElementSummary(
  elementContext?: Record<string, unknown>,
): string | null {
  if (!elementContext) return null;

  const tag = elementContext.elementTag as string;
  const source = elementContext.sourceLocation as {
    fileName?: string;
    lineNumber?: number;
  };

  if (!tag) return null;

  const parts: string[] = [];
  parts.push(`<${tag}>`);

  if (source?.lineNumber) {
    parts.push(`at line ${source.lineNumber}`);
  }

  if (source?.fileName) {
    const fileName = source.fileName.split("/").pop() || source.fileName;
    parts.push(`in ${fileName}`);
  }

  return parts.join(" ");
}

export {
  extractUIPayload,
  extractPartialUIPayload,
  filterToolNoise,
  getTotalThoughtTime,
  getReadFiles,
  getModifiedFiles,
  parseVisualEditorMessage,
  getElementSummary,
};
export type {
  ActivityEvent,
  FileChange,
  NewFile,
  Action,
  ThemeContext,
  PlanContext,
  MediaResult,
  OptisignsAssetItem,
  OptisignsFolderItem,
  OptisignsWriteConfirm,
  CacheTtlContext,
  UIPayload,
  ParsedResponse,
  VisualEditorData,
};
